<ng-container *transloco="let t">
    <div *ngIf="formGroup" [formGroup]="formGroup">
        <div
            fxLayout="row"
            fxLayout.xs="column"
            fxLayoutAlign="start center"
            fxLayoutGap="10px"
            fxFlexFill
        >
            <ng-container *ngFor="let field of fields">
                <div
                    *ngIf="isFieldVisible(field)"
                    [fxFlex]="fields.length === 2 ? '50' : '100'"
                    fxLayoutAlign="start center"
                    fxFlex.xs="100"
                    fxFlex.sm="100"
                    fxFlexFill
                >
                    <!-- BIRTH DATE -->
                    <mat-form-field
                        appearance="outline"
                        class="date-field"
                        *ngIf="field.type === 'birth-date'"
                    >
                        <mat-label>{{
                            t(field.labelKey ?? field.label ?? field.key)
                        }}</mat-label>
                        <input
                            matInput
                            appDateMask
                            [matDatepicker]="birthdatePicker"
                            [formControlName]="field.key"
                            [autocomplete]="'birthDate' | autocomplete"
                            maxlength="10"
                            (dateChange)="
                                setAgeAtEnrollment(
                                    formGroup.get(field.key)?.value
                                )
                            "
                            [required]="isFieldRequired(field)"
                        />
                        <mat-datepicker-toggle
                            matSuffix
                            [for]="birthdatePicker"
                        ></mat-datepicker-toggle>
                        <mat-datepicker #birthdatePicker></mat-datepicker>
                        <mat-hint>MM/DD/YYYY</mat-hint>

                        <mat-error
                            *ngIf="
                                formGroup.get(field.key)?.hasError('required')
                            "
                            >{{ t("shared.error.required.birthdate") }}
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formGroup.get(field.key)?.hasError('minimumAge')
                            "
                            >{{ t("shared.error.min.age") }}
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formGroup.get(field.key)?.hasError('maximumAge')
                            "
                            >{{ t("shared.error.max.age") }}
                        </mat-error>
                    </mat-form-field>

                    <!-- TEXT / NUMBER -->
                    <mat-form-field
                        appearance="outline"
                        *ngIf="field.type === 'text' || field.type === 'number'"
                    >
                        <mat-label
                            >{{ t(field.labelKey ?? field.label ?? field.key) }}
                        </mat-label>
                        <input
                            matInput
                            [type]="field.type === 'number' ? 'number' : 'text'"
                            [formControlName]="field.key"
                            [attr.maxlength]="getMaxLength(field)"
                        />
                        <mat-hint *ngIf="field.hintKey">{{
                            t(field.hintKey)
                        }}</mat-hint>
                        <mat-error
                            *ngFor="let validator of getValidators(field)"
                        >
                            {{
                                t(
                                    validator.messageKey ??
                                        validator.message ??
                                        validator.name
                                ) ??
                                    validator.messageKey ??
                                    validator.message
                            }}
                        </mat-error>
                    </mat-form-field>

                    <!-- EMAIL -->
                    <mat-form-field
                        appearance="outline"
                        *ngIf="field.type === 'email'"
                    >
                        <mat-label>{{
                            t(field.labelKey ?? field.label ?? field.key)
                        }}</mat-label>
                        <input
                            matInput
                            type="email"
                            [formControlName]="field.key"
                            [autocomplete]="'email' | autocomplete"
                            maxlength="200"
                        />
                        <mat-error
                            *ngFor="let validator of getValidators(field)"
                        >
                            {{
                                t(
                                    validator.messageKey ??
                                        validator.message ??
                                        validator.name
                                ) ??
                                    validator.messageKey ??
                                    validator.message
                            }}
                        </mat-error>
                    </mat-form-field>

                    <!-- SELECT -->
                    <mat-form-field
                        appearance="outline"
                        *ngIf="field.type === 'select'"
                        class="full-width"
                    >
                        <mat-label>{{
                            t(field.labelKey ?? field.label ?? field.key)
                        }}</mat-label>
                        <mat-select
                            [formControlName]="field.key"
                            [required]="isFieldRequired(field)"
                            #select
                        >
                            <mat-option>
                                <ngx-mat-select-search
                                    [formControl]="getFilterCtrl(field.key)"
                                    [placeholderLabel]="
                                        t('shared.search-select.search')
                                    "
                                    [noEntriesFoundLabel]="
                                        t(
                                            'shared.search-select.no-entries-found'
                                        )
                                    "
                                    [clearSearchInput]="false"
                                ></ngx-mat-select-search>
                            </mat-option>

                            <mat-option
                                *ngFor="
                                    let selection of (getFilteredOptions(field)
                                        | async) ?? []
                                "
                                [value]="
                                    field.key === 'MailingStateCode'
                                        ? selection.code
                                        : selection.selectionId
                                "
                            >
                                {{ selection.label }}
                            </mat-option>
                        </mat-select>
                        <mat-error
                            *ngFor="let validator of getValidators(field)"
                        >
                            {{
                                t(
                                    validator.messageKey ??
                                        validator.message ??
                                        validator.name
                                ) ??
                                    validator.messageKey ??
                                    validator.message
                            }}
                        </mat-error>
                        <mat-error
                            *ngIf="formGroup.get(field.key)?.hasError('email')"
                            >{{ t("shared.error.email") }}</mat-error
                        >
                    </mat-form-field>

                    <!-- SINGLE-SELECT (SingleSelectionControlComponent) -->
                    <ng-container *ngIf="field.type === 'single-select'">
                        <ng-container *ngIf="field.options$ | async as options">
                            <app-single-selection-control
                                [formGroup]="formGroup"
                                [selections]="options"
                                [formControlName]="field.key"
                                [control]="formGroup.get(field.key)"
                                [label]="
                                    field.labelKey ? t(field.labelKey) : ''
                                "
                                [required]="isFieldRequired(field)"
                                [requiredErrorKey]="getRequiredErrorKey(field)"
                                [formSubmitted]="formSubmitted"
                                [requiredMessage]="
                                    t(
                                        getValidatorMessage(
                                            field,
                                            'requireFieldWhen'
                                        ) ??
                                            getValidatorMessage(
                                                field,
                                                'required'
                                            ) ??
                                            ''
                                    )
                                "
                            >
                            </app-single-selection-control>
                        </ng-container>
                    </ng-container>

                    <!-- MULTI-SELECT (MultiSelectionControlComponent) -->
                    <ng-container *ngIf="field.type === 'multi-select'">
                        <ng-container *ngIf="field.options$ | async as options">
                            <div fxLayout="column" fxFlex="100">
                                <app-multi-selection-control
                                    [formGroup]="formGroup"
                                    [control]="formGroup.get(field.key)"
                                    [selections]="options"
                                    [formControlName]="field.key"
                                    [label]="
                                        t(
                                            field.labelKey ??
                                                field.label ??
                                                field.key
                                        )
                                    "
                                    [required]="isFieldRequired(field)"
                                    [requiredErrorKey]="
                                        getRequiredErrorKey(field)
                                    "
                                    [formSubmitted]="formSubmitted"
                                    [requiredMessage]="
                                        t(
                                            getValidatorMessage(
                                                field,
                                                'requireFieldWhen'
                                            ) ??
                                                getValidatorMessage(
                                                    field,
                                                    'required'
                                                ) ??
                                                ''
                                        )
                                    "
                                >
                                </app-multi-selection-control>

                                <mat-error
                                    *ngFor="
                                        let validator of getValidators(field)
                                    "
                                >
                                    <div *ngIf="validator.name !== 'required'">
                                        {{
                                            t(
                                                validator.messageKey ??
                                                    validator.message ??
                                                    validator.name
                                            ) ??
                                                validator.messageKey ??
                                                validator.message
                                        }}
                                    </div>
                                </mat-error>
                            </div>
                        </ng-container>
                    </ng-container>

                    <!-- SITE-SELECT (MultiSelectionControlComponent for provider sites) -->
                    <ng-container *ngIf="field.type === 'site-select'">
                        <ng-container *ngIf="field.options$ | async as options">
                            <div fxLayout="column" fxFlex="100">
                                <app-multi-selection-control
                                    [formGroup]="formGroup"
                                    [control]="formGroup.get(field.key)"
                                    [selections]="options"
                                    [formControlName]="field.key"
                                    [label]="
                                        t(
                                            field.labelKey ??
                                                field.label ??
                                                field.key
                                        )
                                    "
                                    [required]="isFieldRequired(field)"
                                    [requiredErrorKey]="
                                        getRequiredErrorKey(field)
                                    "
                                    [formSubmitted]="formSubmitted"
                                    [requiredMessage]="
                                        t(
                                            getValidatorMessage(
                                                field,
                                                'requireFieldWhen'
                                            ) ??
                                                getValidatorMessage(
                                                    field,
                                                    'required'
                                                ) ??
                                                ''
                                        )
                                    "
                                >
                                </app-multi-selection-control>

                                <mat-error
                                    *ngFor="
                                        let validator of getValidators(field)
                                    "
                                >
                                    <div *ngIf="validator.name !== 'required'">
                                        {{
                                            t(
                                                validator.messageKey ??
                                                    validator.message ??
                                                    validator.name
                                            ) ??
                                                validator.messageKey ??
                                                validator.message
                                        }}
                                    </div>
                                </mat-error>
                            </div>
                        </ng-container>
                    </ng-container>

                    <!-- POSTAL CODE -->
                    <mat-form-field
                        appearance="outline"
                        *ngIf="field.type === 'postal-code'"
                    >
                        <mat-label>{{
                            t(field.labelKey ?? field.label ?? field.key)
                        }}</mat-label>
                        <input
                            matInput
                            [formControlName]="field.key"
                            [autocomplete]="'postalCode' | autocomplete"
                            maxlength="20"
                            [required]="isFieldRequired(field)"
                            inputmode="numeric"
                        />
                        <mat-error
                            *ngFor="let validator of getValidators(field)"
                        >
                            {{
                                t(
                                    validator.messageKey ??
                                        validator.message ??
                                        validator.name
                                ) ??
                                    validator.messageKey ??
                                    validator.message
                            }}
                        </mat-error>
                    </mat-form-field>

                    <!-- SSN INPUT -->
                    <mat-form-field
                        appearance="outline"
                        *ngIf="field.type === 'ssn'"
                    >
                        <mat-label>{{
                            t(field.labelKey ?? field.label ?? field.key)
                        }}</mat-label>
                        <app-ssn-input
                            [formControlName]="field.key"
                            #ssn
                            [required]="isFieldRequired(field)"
                        ></app-ssn-input>
                        <!-- <mat-error
                        *ngIf="
                            !formGroup.get(field.key)?.hasError('required') &&
                            ssn.errorState
                        "
                        >{{ t("shared.error.pattern.ssn") }}
                    </mat-error> -->
                        <mat-hint>###-##-####</mat-hint>
                        <mat-error
                            *ngFor="let validator of getValidators(field)"
                        >
                            {{
                                t(
                                    validator.messageKey ??
                                        validator.message ??
                                        validator.name
                                ) ??
                                    validator.messageKey ??
                                    validator.message
                            }}
                        </mat-error>
                        <mat-error
                            *ngIf="
                                !formGroup
                                    .get(field.key)
                                    ?.hasError('required') &&
                                formGroup.get(field.key)?.hasError('pattern')
                            "
                            >{{ t("shared.error.pattern.ssn") }}
                        </mat-error>
                    </mat-form-field>

                    <!-- PHONE INPUT -->
                    <mat-form-field
                        appearance="outline"
                        *ngIf="field.type === 'phone'"
                    >
                        <mat-label>{{
                            t(field.labelKey ?? field.label ?? field.key)
                        }}</mat-label>
                        <app-phone-input
                            *ngIf="field.key !== 'WorkPhone'"
                            [formControlName]="field.key"
                            #phoneInput
                            [required]="isFieldRequired(field)"
                        ></app-phone-input>
                        <app-phone-input
                            *ngIf="field.key === 'WorkPhone'"
                            [formControlName]="field.key"
                            #phoneInput
                            [required]="isFieldRequired(field)"
                            [showExtension]="true"
                        ></app-phone-input>
                        <mat-error
                            *ngFor="let validator of getValidators(field)"
                        >
                            {{
                                t(
                                    validator.messageKey ??
                                        validator.message ??
                                        validator.name
                                ) ??
                                    validator.messageKey ??
                                    validator.message
                            }}
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formGroup.get(field.key)?.hasError('pattern')
                            "
                            >{{ t("shared.error.pattern.phone") }}</mat-error
                        >
                    </mat-form-field>

                    <!-- CHECKBOX -->
                    <ng-container *ngIf="field.type === 'checkbox'">
                        <mat-checkbox
                            [id]="field.key"
                            class="checkbox-signature full-width"
                            [formControlName]="field.key"
                        >
                            {{ t(field.labelKey ?? field.label ?? field.key) }}
                        </mat-checkbox>
                        <mat-error
                            *ngFor="let validator of getValidators(field)"
                        >
                            {{
                                t(
                                    validator.messageKey ??
                                        validator.message ??
                                        validator.name
                                ) ??
                                    validator.messageKey ??
                                    validator.message
                            }}
                        </mat-error>
                    </ng-container>

                    <!-- SIGNATURE DATE-->
                    <mat-form-field
                        appearance="outline"
                        class="date-field"
                        *ngIf="field.type === 'signature-date'"
                    >
                        <mat-label>{{
                            t("shared.field.currentDate")
                        }}</mat-label>
                        <input
                            matInput
                            appDateMask
                            [matDatepicker]="signaturePicker"
                            [formControlName]="field.key"
                            maxlength="10"
                            [required]="isFieldRequired(field)"
                        />
                        <mat-error
                            *ngIf="
                                formGroup.get(field.key)?.hasError('required')
                            "
                        >
                            {{ t("shared.error.required.currentDate") }}
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formGroup
                                    .get(field.key)
                                    ?.hasError('invalidDate')
                            "
                        >
                            {{ t("shared.error.signatureDate") }}
                        </mat-error>
                        <mat-datepicker-toggle
                            matSuffix
                            [for]="signaturePicker"
                        ></mat-datepicker-toggle>
                        <mat-datepicker #signaturePicker></mat-datepicker>
                        <mat-hint>MM/DD/YYYY</mat-hint>
                    </mat-form-field>

                    <!-- FUTURE YEAR DATE -->
                    <mat-form-field
                        appearance="outline"
                        class="date-field"
                        *ngIf="field.type === 'future-year-date'"
                    >
                        <mat-label>{{
                            t(field.labelKey ?? field.label ?? field.key)
                        }}</mat-label>
                        <input
                            matInput
                            type="number"
                            [formControlName]="field.key"
                            [autocomplete]="field.key | autocomplete"
                            min="{{ currentYear - 100 }}"
                            max="{{ currentYear }}"
                            maxlength="4"
                            placeholder="{{ currentYear }}"
                            [required]="isFieldRequired(field)"
                        />
                        <mat-error
                            *ngIf="
                                formGroup.get(field.key)?.hasError('pattern')
                            "
                            >{{
                                t("shared.error.pattern.future-year-date")
                            }}</mat-error
                        >
                        <mat-error
                            *ngIf="
                                formGroup.get(field.key)?.hasError('required')
                            "
                        >
                            {{ t("shared.error.required.last-school-year") }}
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formGroup.get(field.key)?.hasError('futureYear')
                            "
                            >{{ t("shared.error.future-year") }}
                        </mat-error>
                    </mat-form-field>

                    <!-- INCOME -->
                    <mat-form-field
                        appearance="outline"
                        *ngIf="field.type === 'income'"
                    >
                        <mat-icon matPrefix>attach_money</mat-icon>
                        <mat-label>{{
                            field.labelKey ? t(field.labelKey) : ""
                        }}</mat-label>
                        <input
                            matInput
                            [formControlName]="field.key"
                            [autocomplete]="field.key | autocomplete"
                            type="number"
                            min="0"
                            max="10000000"
                            oninput="value = !!value && value < 0 ? value*-1 : value"
                            [required]="isFieldRequired(field)"
                        />
                        <mat-error
                            *ngIf="
                                formGroup.get(field.key)?.hasError('required')
                            "
                        >
                            {{ t("shared.error.required.your-income") }}
                        </mat-error>
                    </mat-form-field>

                    <!-- SIGNATURE -->
                    <ng-container *ngIf="field.type === 'signature'">
                        <div fxLayout="column" fxLayout.xs="column">
                            <div>
                                <p
                                    class="sub-title"
                                    [id]="field.key + '-signature-label'"
                                >
                                    {{
                                        t(
                                            "application.disclosure.student-signature-label"
                                        )
                                    }}
                                </p>
                            </div>
                            <div>
                                <mat-checkbox
                                    [id]="field.key"
                                    class="checkbox-signature"
                                    [formControlName]="field.key"
                                    [attr.aria-describedby]="
                                        getAriaDescribedBy(field.key)
                                    "
                                >
                                    {{
                                        t(
                                            field.labelKey ??
                                                field.label ??
                                                field.key
                                        )
                                    }}
                                </mat-checkbox>
                                <mat-error
                                    *ngFor="
                                        let validator of getValidators(field)
                                    "
                                >
                                    {{
                                        t(
                                            validator.messageKey ??
                                                validator.message ??
                                                validator.name
                                        ) ??
                                            validator.messageKey ??
                                            validator.message
                                    }}
                                </mat-error>
                            </div>
                            <div
                                class="signature-line"
                                *ngIf="formGroup.get(field.key)?.value"
                            >
                                <mat-label class="signature">
                                    {{
                                        formGroup.get(
                                            "StudentSignatureFirstName"
                                        )?.value
                                            | fullName
                                                : formGroup.get(
                                                      "StudentSignatureLastName"
                                                  )?.value
                                    }}
                                </mat-label>
                                <mat-divider></mat-divider>
                            </div>
                        </div>
                    </ng-container>
                </div>
            </ng-container>
        </div>
    </div>
</ng-container>
